import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Search, X, ChevronDown } from 'lucide-react';

/**
 * Lightweight searchable combobox tuned for the 5-Star Initiate-Review flow.
 *
 * - Filters as you type (case-insensitive, matches display_name + email)
 * - Shows the first ~50 matches; hides the rest behind a "show more" tail
 * - Keyboard friendly: ArrowUp/Down to move, Enter to pick, Esc to clear
 * - Designed for ~5,000+ option lists without re-rendering them all when closed
 */
const HIGHLIGHT = 'bg-fuchsia-100 text-fuchsia-900 font-semibold';
const MAX_VISIBLE = 50;

const renderHighlighted = (label, q) => {
  if (!q) return label;
  const i = label.toLowerCase().indexOf(q.toLowerCase());
  if (i < 0) return label;
  return (
    <>
      {label.slice(0, i)}
      <mark className={HIGHLIGHT + ' rounded px-0.5'}>{label.slice(i, i + q.length)}</mark>
      {label.slice(i + q.length)}
    </>
  );
};

export const EmployeeTypeahead = ({
  value,                  // selected id
  onChange,               // (id) => void
  options,                // [{ id, email, display_name }]
  placeholder = 'Search by name or email…',
  testId = 'employee-typeahead',
  disabled = false,
}) => {
  const [open, setOpen] = useState(false);
  const [q, setQ] = useState('');
  const [activeIdx, setActiveIdx] = useState(0);
  const wrapRef = useRef(null);
  const inputRef = useRef(null);
  const listRef = useRef(null);

  const selected = useMemo(() => options.find(o => o.id === value) || null, [options, value]);

  const filtered = useMemo(() => {
    const needle = q.trim().toLowerCase();
    if (!needle) return options;
    return options.filter(o =>
      (o.display_name || '').toLowerCase().includes(needle) ||
      (o.email || '').toLowerCase().includes(needle)
    );
  }, [options, q]);

  const visible = filtered.slice(0, MAX_VISIBLE);
  const overflow = Math.max(0, filtered.length - visible.length);

  useEffect(() => {
    const onDocClick = (e) => {
      if (wrapRef.current && !wrapRef.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener('mousedown', onDocClick);
    return () => document.removeEventListener('mousedown', onDocClick);
  }, []);

  useEffect(() => { if (open) setActiveIdx(0); }, [open, q]);

  const choose = (opt) => {
    onChange(opt.id);
    setQ('');
    setOpen(false);
  };

  const clear = () => {
    onChange('');
    setQ('');
    inputRef.current?.focus();
  };

  const onKeyDown = (e) => {
    if (!open) {
      if (['ArrowDown', 'Enter'].includes(e.key)) { setOpen(true); e.preventDefault(); }
      return;
    }
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setActiveIdx(i => Math.min(visible.length - 1, i + 1));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setActiveIdx(i => Math.max(0, i - 1));
    } else if (e.key === 'Enter') {
      e.preventDefault();
      if (visible[activeIdx]) choose(visible[activeIdx]);
    } else if (e.key === 'Escape') {
      setOpen(false);
    }
  };

  return (
    <div ref={wrapRef} className="relative" data-testid={testId}>
      <div
        className={`flex items-center gap-2 px-3 py-2 rounded-md border bg-white focus-within:border-fuchsia-400 focus-within:ring-1 focus-within:ring-fuchsia-200 ${disabled ? 'bg-slate-50 border-slate-200' : 'border-slate-300 cursor-text'}`}
        onClick={() => !disabled && (setOpen(true), inputRef.current?.focus())}
      >
        <Search className="w-3.5 h-3.5 text-slate-400 flex-shrink-0" />
        {selected && !q ? (
          <button
            type="button"
            disabled={disabled}
            onClick={(e) => { e.stopPropagation(); setOpen(true); setQ(''); inputRef.current?.focus(); }}
            className="text-sm text-slate-800 truncate text-left flex-1"
            data-testid={`${testId}-selected`}
          >
            <span className="font-semibold">{selected.display_name}</span>
            <span className="text-slate-500"> · {selected.email}</span>
          </button>
        ) : (
          <input
            ref={inputRef}
            type="text"
            value={q}
            onChange={e => { setQ(e.target.value); setOpen(true); }}
            onFocus={() => setOpen(true)}
            onKeyDown={onKeyDown}
            placeholder={placeholder}
            disabled={disabled}
            className="flex-1 min-w-0 bg-transparent text-sm focus:outline-none disabled:cursor-not-allowed"
            data-testid={`${testId}-input`}
          />
        )}
        {selected && !disabled && (
          <button
            type="button"
            onClick={(e) => { e.stopPropagation(); clear(); }}
            className="p-0.5 rounded hover:bg-slate-100 text-slate-400 hover:text-slate-600"
            data-testid={`${testId}-clear`}
            title="Clear selection"
          >
            <X className="w-3.5 h-3.5" />
          </button>
        )}
        <ChevronDown className={`w-3.5 h-3.5 text-slate-400 transition-transform ${open ? 'rotate-180' : ''}`} />
      </div>

      {open && !disabled && (
        <div
          ref={listRef}
          className="absolute left-0 right-0 top-full mt-1 z-20 max-h-72 overflow-y-auto rounded-lg border border-slate-200 bg-white shadow-xl"
          data-testid={`${testId}-list`}
        >
          {visible.length === 0 ? (
            <div className="px-3 py-4 text-xs text-slate-400 italic text-center">No matches.</div>
          ) : visible.map((opt, i) => (
            <button
              key={opt.id}
              type="button"
              onClick={() => choose(opt)}
              onMouseEnter={() => setActiveIdx(i)}
              data-testid={`${testId}-opt-${opt.id}`}
              className={`w-full text-left px-3 py-2 text-sm flex flex-col gap-0 border-b border-slate-50 last:border-b-0 ${i === activeIdx ? 'bg-fuchsia-50' : 'hover:bg-slate-50'}`}
            >
              <span className="font-semibold text-slate-800 truncate">{renderHighlighted(opt.display_name || opt.email, q)}</span>
              <span className="text-[11px] text-slate-500 truncate">{renderHighlighted(opt.email, q)}</span>
            </button>
          ))}
          {overflow > 0 && (
            <div className="px-3 py-2 text-[11px] text-center text-slate-400 italic bg-slate-50 border-t border-slate-100">
              …{overflow} more match{overflow === 1 ? '' : 'es'} — keep typing to narrow.
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default EmployeeTypeahead;
